/**
 * @file RepContracts.js
 * @version 1.1.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-30
 * @description The reps' contracts screen — "My Contracts" for a rep,
 *              "Reps' Contracts" for the office (Cam, 28 Oct 2026 — O4: "a
 *              view for reps so they can see contracts for their customers,
 *              which should allow them to see orders from contracts, product
 *              groups, costing, pricing, margin info, filterable by product
 *              type, customer, pricing period, fulfillment, works on mobile
 *              (as reps will be using it primarily on mobile) as well as
 *              laptop").
 *
 *   Decision D12 (proposal https://claude.ai/artifact/Dtwm64fYbke3T4Y2jpvjPR):
 *   GROUPED BY CUSTOMER — each customer a section (on a phone a card, tap to
 *   open), its contracts as rows: product (a group of mills opens into its
 *   suppliers' products), period, state, price per its unit, cost and margin
 *   (£ and % on cost — a rep sees them on their own customers only; the
 *   server decides, cost_visibility), and how far it has got. Cam's note:
 *   "Standard view is filtered to the current period, and we don't need the
 *   bar if the contract does not have a tonnage on it (most don't) - for
 *   those just show the order count and total tonnage (be clear on the unit
 *   here …)". Tap a contract for its orders (RepContractDetail, GET
 *   /api/rep-contracts/contract?key=).
 *
 *   Filters along the top on a laptop; on the rep phone shell a Filters
 *   button opens them in a sheet. Remembered per tab (usePersistedState).
 *   Office users choose a rep (rep_id — what that rep sees; cost per their
 *   own "See cost & margin").
 *
 *   Read only: nothing here writes.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../config';
import './RepContracts.css';
import './TableStyles.css';
import Input from './Input';
import Dropdown from './Dropdown';
import SearchableDropdown from './SearchableDropdown';
import SearchHelp from './SearchHelp';
import Modal from './Modal';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import RepContractDetail from './RepContractDetail';
import { useEntityModal } from './EntityModalProvider';
import { useSlideOutButton } from './SlideOutButton';
import usePersistedState from '../hooks/usePersistedState';
import useLatestRequest from '../hooks/useLatestRequest';
import useRepMobile from '../hooks/useRepMobile';
import { clearFiltersClass } from '../utils/activeFilters';
import { formatTonnes } from '../utils/numberFormatters';
import {
  CATEGORY_ORDER,
  DEFAULT_STATES,
  STATE_LABELS,
  activeFilterWords,
  barOf,
  contractMeta,
  customerSummaryText,
  datesText,
  defaultFilters,
  filterCount,
  filtersApplied,
  isMultiProduct,
  listQuery,
  marginView,
  priceOrRange,
  toggleIn,
  usageLines,
  whenText
} from '../utils/repContractsView';

const SEARCH_FIELDS = ['customer', 'account no.', 'product', 'item code', 'holding product', 'supplier',
  'contract numbers', 'period', 'product type', 'product group', 'an order number on it', 'the price'];

const FLAG_TONE = {
  no_price: 'error',
  negative_margin: 'error',
  unfulfilled: 'error',
  no_cost: 'warning',
  product_archived: 'warning',
  utilise: 'warning'
};

async function getJson(url, signal) {
  const res = await fetch(url, { credentials: 'include', signal });
  let body = null;
  try { body = await res.json(); } catch (e) { body = null; }
  if (!res.ok || !body || body.success === false) {
    throw new Error((body && body.error) || `The server answered ${res.status}`);
  }
  return body;
}

/** A chip row: each value with its count; several may be chosen. */
function ChipRow({ label, items, chosen, counts, onToggle, allLabel, onAll }) {
  return (
    <div className="rc-chip-group" role="group" aria-label={label}>
      <span className="rc-chip-label">{label}</span>
      <div className="rc-chips">
        {onAll && (
          <button type="button" data-read-safe className={`rc-chip${chosen.length ? '' : ' is-on'}`}
            aria-pressed={!chosen.length} onClick={onAll}>
            {allLabel}
          </button>
        )}
        {items.map(it => {
          const on = chosen.includes(it.key);
          const n = counts ? (counts[it.key] || 0) : null;
          return (
            <button key={it.key} type="button" data-read-safe
              className={`rc-chip${on ? ' is-on' : ''}${n === 0 && !on ? ' is-empty' : ''}`}
              aria-pressed={on} onClick={() => onToggle(it.key)}>
              {it.label}{n !== null && <span className="rc-chip-count">{n}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** "£387.00–£394.00 /T" — the unit never wraps away from its figure. */
function FigWithUnit({ text }) {
  const i = text.lastIndexOf(' /');
  if (i < 0) return text;
  return <>{text.slice(0, i)} <span className="rc-unit">{text.slice(i + 1)}</span></>;
}

/** The phone's pager: two thumb-sized buttons and where you are. */
function PhonePager({ paging, onPage }) {
  if (!paging || paging.pages <= 1) return null;
  const from = (paging.page - 1) * paging.limit + 1;
  const to = Math.min(paging.page * paging.limit, paging.total);
  return (
    <div className="rc-phone-pager">
      <button type="button" data-read-safe className="rc-page-btn" disabled={paging.page <= 1}
        onClick={() => onPage(paging.page - 1)} aria-label="Previous page">
        <span className="material-symbols-rounded" aria-hidden="true">chevron_left</span>
      </button>
      <span className="rc-page-where">Customers {from}–{to} of {paging.total}</span>
      <button type="button" data-read-safe className="rc-page-btn" disabled={paging.page >= paging.pages}
        onClick={() => onPage(paging.page + 1)} aria-label="Next page">
        <span className="material-symbols-rounded" aria-hidden="true">chevron_right</span>
      </button>
    </div>
  );
}

function ContractRow({ c, open, onToggle, showCosts }) {
  const usage = usageLines(c);
  const bar = barOf(c);
  const margin = showCosts ? marginView(c) : null;
  const price = priceOrRange(c.price, c.price_range, c.price_unit);
  const cost = showCosts ? priceOrRange(c.cost, c.cost_range, c.price_unit) : '';
  const multi = isMultiProduct(c);
  const when = whenText(c);
  // One product in a product group says so; a contract of several products
  // (a group of mills) has the stacks icon and opens into them.
  // A contract made from a product group says so whatever it holds (29 Oct 2026).
  const productGroup = c.made_from_group && c.made_from_group.name ? c.made_from_group.name
    : (!multi && (c.products || []).length === 1 && c.products[0].product_group
      ? c.products[0].product_group.name : null);
  const notes = (c.usage && c.usage.notes) || [];
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); }
  };
  return (
    <div
      className={`rc-contract${open ? ' is-open' : ''}${c.state !== 'live' ? ` is-${c.state}` : ''}${showCosts ? '' : ' no-costs'}`}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={onKey}
      data-read-safe
    >
      <div className="rc-c-product">
        <div className="rc-c-title">
          {multi && (
            <span className="material-symbols-rounded rc-c-icon" aria-hidden="true"
              title={c.made_from_group ? `Made from the product group ${c.made_from_group.name}` : c.product_group ? `Product group: ${c.product_group.name}` : 'Several products'}>stacks</span>
          )}
          <span className="rc-c-name">{c.title}</span>
        </div>
        <div className="rc-c-meta">{contractMeta(c)}</div>
        <div className="rc-c-badges">
          {c.state !== 'live' && <span className={`rc-pill rc-pill-${c.state}`}>{STATE_LABELS[c.state]}</span>}
          {when && <span className="rc-pill">{when}</span>}
          <span className="rc-c-dates">{datesText(c.start_date, c.end_date)}</span>
          {productGroup && <span className="rc-pill rc-pill-group">Group: {productGroup}</span>}
          {/* The price cell already says "No price" when there is none. */}
          {(c.flags || []).filter(f => f.key !== 'no_price' || price).map(f => (
            <span key={f.key} className={`rc-pill rc-pill-${FLAG_TONE[f.key] || 'warning'}`}>{f.text}</span>
          ))}
        </div>
      </div>
      <div className="rc-c-usage">
        {bar && (
          <div className={`rc-bar tone-${bar.tone}`} role="img" aria-label={c.fulfilment.text}>
            <i style={{ width: `${bar.width}%` }} />
          </div>
        )}
        <div className="rc-c-usage-main">{usage.main}</div>
        {usage.sub && <div className="rc-c-usage-sub">{usage.sub}</div>}
        {notes.map(n => (
          <div key={n.key} className={`rc-unit-note tone-${n.tone}`}>
            <span className="material-symbols-rounded" aria-hidden="true">{n.tone === 'warning' ? 'warning' : 'scale'}</span>
            {n.text}
          </div>
        ))}
      </div>
      <div className="rc-c-figs">
        <div className="rc-fig">
          <span className={`rc-fig-value${!price ? ' tone-error' : ''}`}>{price ? <FigWithUnit text={price} /> : 'No price'}</span>
          <span className="rc-fig-label">price</span>
        </div>
        {showCosts && (
          <div className="rc-fig">
            <span className="rc-fig-value">{cost ? <FigWithUnit text={cost} /> : '—'}</span>
            <span className="rc-fig-label">cost</span>
          </div>
        )}
        {showCosts && (
          <div className="rc-fig">
            {margin ? (
              <>
                <span className={`rc-fig-value tone-${margin.tone}`}>{margin.amount}</span>
                <span className="rc-fig-label">{margin.percent || 'margin'}</span>
              </>
            ) : (
              <>
                <span className="rc-fig-value">—</span>
                <span className="rc-fig-label">margin</span>
              </>
            )}
          </div>
        )}
      </div>
      <span className="material-symbols-rounded rc-c-chevron" aria-hidden="true">{open ? 'expand_less' : 'expand_more'}</span>
    </div>
  );
}

function RepContracts() {
  const phone = useRepMobile();
  const { openEntity } = useEntityModal();
  const latest = useLatestRequest();

  const [options, setOptions] = useState(null);
  const [optionsError, setOptionsError] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [details, setDetails] = useState({});

  const [repId, setRepId] = usePersistedState('repContracts.repId', '');
  const [filters, setFilters] = usePersistedState('repContracts.filters', defaultFilters(null));
  const [page, setPage] = usePersistedState('repContracts.page', 1);
  // { [account_id]: true|false } — differs from the default (open on a
  // laptop, closed on a phone) only where someone tapped it.
  const [openCustomers, setOpenCustomers] = usePersistedState('repContracts.openCustomers', {});
  const [openContracts, setOpenContracts] = usePersistedState('repContracts.openContracts', []);
  const [debouncedQ, setDebouncedQ] = useState(filters.q || '');

  useSlideOutButton([phone, sheetOpen, !!options]);

  // ── Options: whose customers, which periods, the current one ──────────────
  useEffect(() => {
    const ctrl = new AbortController();
    setOptionsError(null);
    const q = repId !== '' && repId !== null ? `?rep_id=${encodeURIComponent(repId)}` : '';
    getJson(`${API_URL}/api/rep-contracts/options${q}`, ctrl.signal)
      .then(body => setOptions(body))
      .catch(e => { if (e.name !== 'AbortError') setOptionsError(e.message); });
    return () => ctrl.abort();
  }, [repId]);

  const defaultPeriod = options ? (options.default_period || '') : null;

  // The standard view is the current period — put it in on first use (and
  // never over a period someone chose).
  useEffect(() => {
    if (options && filters.period === null) {
      setFilters(f => ({ ...f, period: options.default_period || '' }));
    }
  }, [options, filters.period, setFilters]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q || ''), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  // ── The list ──────────────────────────────────────────────────────────────
  const query = useMemo(() => (
    filters.period === null ? null : listQuery({ ...filters, q: debouncedQ }, { repId, page })
  ), [filters, debouncedQ, repId, page]);

  const load = useCallback(() => {
    if (!query) return;
    const ticket = latest.begin();
    setLoading(true);
    setError(null);
    getJson(`${API_URL}/api/rep-contracts?${query}`)
      .then(body => { if (latest.isLatest(ticket)) { setData(body); setLoading(false); } })
      .catch(e => { if (latest.isLatest(ticket)) { setError(e.message); setLoading(false); } });
  }, [query, latest]);

  useEffect(() => { load(); }, [load]);

  // A page past the end (fewer customers after a filter) goes back to the last.
  useEffect(() => {
    if (data && data.paging && page > data.paging.pages) setPage(data.paging.pages);
  }, [data, page, setPage]);

  // ── An opened contract's orders ───────────────────────────────────────────
  const detailKey = (key) => `${repId}|${key}`;
  const fetchDetail = useCallback((key) => {
    const k = `${repId}|${key}`;
    setDetails(d => ({ ...d, [k]: { loading: true } }));
    getJson(`${API_URL}/api/rep-contracts/contract?key=${encodeURIComponent(key)}`)
      .then(body => setDetails(d => ({ ...d, [k]: { data: body.contract } })))
      .catch(e => setDetails(d => ({ ...d, [k]: { error: e.message } })));
  }, [repId]);

  const openSet = useMemo(() => new Set(openContracts || []), [openContracts]);
  useEffect(() => {
    for (const key of openSet) {
      if (!details[detailKey(key)]) fetchDetail(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSet, fetchDetail]);

  const toggleContract = (key) => {
    setOpenContracts(list => (list || []).includes(key) ? list.filter(k => k !== key) : [...(list || []), key]);
  };
  const customerOpen = (id) => (Object.prototype.hasOwnProperty.call(openCustomers || {}, id) ? !!openCustomers[id] : !phone);
  const toggleCustomer = (id) => setOpenCustomers(m => ({ ...(m || {}), [id]: !customerOpen(id) }));

  // ── Filters ───────────────────────────────────────────────────────────────
  const setFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);
  };
  const clearFilters = () => {
    setFilters(defaultFilters(defaultPeriod ?? ''));
    setDebouncedQ('');
    setPage(1);
  };
  const applied = options ? filtersApplied(filters, defaultPeriod) : false;
  const changeRep = (value) => {
    setRepId(value === null || value === undefined ? '' : String(value));
    setFilters(f => ({ ...f, accountId: '' }));
    setPage(1);
    setDetails({});
  };

  const counts = (data && data.counts) || {};
  const showCosts = data ? !!(data.cost_visibility && data.cost_visibility.shown) : true;
  const scoped = options ? !!options.scoped : true;

  const categoryItems = useMemo(() => {
    const cats = (options && options.categories) || [];
    return CATEGORY_ORDER.filter(k => cats.some(c => c.key === k))
      .map(k => ({ key: k, label: cats.find(c => c.key === k).label }));
  }, [options]);
  const fulfilmentItems = useMemo(() => ((options && options.fulfilment) || []).map(b => ({ key: b.key, label: b.label })), [options]);
  const stateItems = useMemo(() => ((options && options.states) || []).map(s => ({ key: s.key, label: s.label })), [options]);

  const customerOptions = useMemo(() => [
    { value: '', label: 'All customers' },
    ...((options && options.customers) || []).map(c => ({
      value: String(c.account_id),
      label: c.name || `Account ${c.account_id}`,
      badge: c.account_no || undefined,
      searchText: `${c.account_no || ''} ${c.name || ''}`
    }))
  ], [options]);
  const periodOptions = useMemo(() => [
    { value: '', label: 'Any period' },
    ...((options && options.periods) || []).map(p => ({
      value: p.name,
      label: `${p.name}${p.in_force === 'current' ? ' (now)' : ''}`
    }))
  ], [options]);
  const repOptions = useMemo(() => [
    { value: '', label: 'All reps' },
    ...((options && options.reps) || []).map(r => ({
      value: String(r.id),
      label: r.name,
      tag: r.active ? undefined : 'Not active',
      searchText: r.name
    }))
  ], [options]);

  const filterControls = (
    <>
      {!scoped && (
        <div className="rc-field rc-field-rep">
          <label>Rep</label>
          <SearchableDropdown options={repOptions} value={repId === '' ? '' : String(repId)}
            onChange={changeRep} placeholder="All reps" searchPlaceholder="Find a rep…" />
        </div>
      )}
      {!phone && (
      <div className="rc-field rc-field-search">
        <label>
          Search
          <SearchHelp fields={SEARCH_FIELDS} amounts />
        </label>
        <Input type="text" icon="search" placeholder="Customer, product, contract no., SO-D…"
          value={filters.q || ''} onChange={(e) => setFilter('q', e.target.value)} />
      </div>
      )}
      <div className="rc-field">
        <label>Customer</label>
        <SearchableDropdown options={customerOptions} value={filters.accountId ? String(filters.accountId) : ''}
          onChange={(v) => setFilter('accountId', v ? String(v) : '')}
          placeholder="All customers" searchPlaceholder="Name or account no…" />
      </div>
      <div className="rc-field">
        <label>Pricing period</label>
        <Dropdown options={periodOptions} value={filters.period || ''}
          onChange={(v) => setFilter('period', v || '')} placeholder="Any period" />
      </div>
    </>
  );

  const chipControls = (
    <>
      <ChipRow label="Product type" items={categoryItems} chosen={filters.category || []} counts={counts.category}
        allLabel="All types" onAll={() => setFilter('category', [])}
        onToggle={(k) => setFilter('category', toggleIn(filters.category || [], k, CATEGORY_ORDER))} />
      <ChipRow label="Fulfilment" items={fulfilmentItems} chosen={filters.fulfilment || []} counts={counts.fulfilment}
        allLabel="Any" onAll={() => setFilter('fulfilment', [])}
        onToggle={(k) => setFilter('fulfilment', toggleIn(filters.fulfilment || [], k, fulfilmentItems.map(i => i.key)))} />
      <ChipRow label="State" items={stateItems} chosen={filters.state || []} counts={counts.state}
        onToggle={(k) => {
          const next = toggleIn(filters.state || [], k, stateItems.map(i => i.key));
          setFilter('state', next.length ? next : [...DEFAULT_STATES]);
        }} />
    </>
  );

  const clearButton = (
    <button type="button" data-read-safe className={clearFiltersClass(applied)} onClick={clearFilters}
      title={applied ? 'Back to the standard view: this period, live and on hold' : 'Showing the standard view'}>
      <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
      <span className="btn-label">Clear Filters</span>
    </button>
  );

  const totals = data && data.totals;
  const who = data && data.rep ? data.rep.name : (scoped ? 'Your customers' : 'All reps');
  const headline = totals
    ? `${who} · ${totals.contracts} contract${totals.contracts === 1 ? '' : 's'} · ${totals.customers} customer${totals.customers === 1 ? '' : 's'}`
    : who;
  const paging = data && data.paging;
  const customers = (data && data.customers) || [];
  const nFilters = options ? filterCount(filters, defaultPeriod) : 0;
  // Back to the first customer after changing page: the list scrolls in its
  // own panel on a laptop, in the page (.content-inner) on a phone.
  const rootRef = useRef(null);
  const toTop = () => {
    const root = rootRef.current;
    if (!root) return;
    const results = root.querySelector('.rc-results');
    if (results && !phone) results.scrollTop = 0;
    else if (root.scrollIntoView) root.scrollIntoView({ block: 'start' });
  };

  if (optionsError && !options) {
    return (
      <div className="rep-contracts-screen">
        <div className="rc-error" role="alert">Couldn’t load the contracts: {optionsError}</div>
      </div>
    );
  }

  return (
    <div className={`rep-contracts-screen${phone ? ' is-phone' : ''}`} ref={rootRef}>
      {phone ? (
        <div className="rc-phone-top">
          <div className="rc-phone-bar">
            <Input type="text" icon="search" placeholder="Search contracts"
              value={filters.q || ''} onChange={(e) => setFilter('q', e.target.value)} aria-label="Search contracts" />
            <button type="button" data-read-safe className={`rc-filters-btn${nFilters ? ' has-filters' : ''}`}
              onClick={() => setSheetOpen(true)} aria-haspopup="dialog">
              <span className="material-symbols-rounded" aria-hidden="true">tune</span>
              Filters{nFilters ? <span className="rc-chip-count">{nFilters}</span> : null}
            </button>
          </div>
          <button type="button" data-read-safe className="rc-phone-summary" onClick={() => setSheetOpen(true)}>
            {activeFilterWords(filters, { options }).join(' · ')}
          </button>
        </div>
      ) : (
        <div className="rc-filters">
          <div className="rc-filter-row">
            {filterControls}
            <div className="rc-field rc-field-clear">
              <div className="buttons slideout"><div>{clearButton}</div></div>
            </div>
          </div>
          <div className="rc-filter-row rc-chip-rows">{chipControls}</div>
        </div>
      )}

      <div className="rc-headline">
        <span className="rc-headline-main">{headline}</span>
        {filters.period ? <span className="rc-headline-period">{filters.period}</span> : null}
        {data && data.cost_visibility && data.cost_visibility.text && (
          <span className={`rc-headline-note${data.cost_visibility.shown ? '' : ' is-off'}`}>
            <span className="material-symbols-rounded" aria-hidden="true">{data.cost_visibility.shown ? 'visibility' : 'visibility_off'}</span>
            {data.cost_visibility.text}
          </span>
        )}
        {data && data.ready && data.ready.links === false && data.ready.links_text && (
          <span className="rc-headline-note is-off">{data.ready.links_text}</span>
        )}
      </div>

      {paging && paging.pages > 1 && !phone && (
        <div className="rc-pager">
          <PaginationControls
            pagination={{ page: paging.page, limit: paging.limit, total: paging.total, totalPages: paging.pages }}
            onPageChange={(p) => setPage(p)}
          />
        </div>
      )}

      <div className={`rc-results${loading && data ? ' is-loading' : ''}`} aria-busy={loading}>
        {error && <div className="rc-error" role="alert">Couldn’t load the contracts: {error}</div>}
        {loading && !data && <LoadingSpinner />}
        {data && !customers.length && !error && (
          <div className="rc-empty">
            <p>No contracts match these filters.</p>
            {applied && (
              <button type="button" data-read-safe className="rc-link" onClick={clearFilters}>Back to the standard view</button>
            )}
          </div>
        )}
        {customers.map(cu => {
          const isOpen = customerOpen(cu.account_id);
          return (
            <section key={cu.account_id} className={`rc-customer${isOpen ? ' is-open' : ''}`}>
              <div className="rc-cust-head">
                <button type="button" data-read-safe className="rc-cust-toggle" aria-expanded={isOpen}
                  onClick={() => toggleCustomer(cu.account_id)}>
                  <span className="rc-cust-name-line">
                    {cu.account_no && <span className="account-badge-inline">{cu.account_no}</span>}
                    <span className="rc-cust-name">{cu.name}</span>
                  </span>
                  <span className="rc-cust-summary">
                    {customerSummaryText(cu, { formatTonnes })}
                    {cu.summary && cu.summary.no_price > 0 && (
                      <span className="rc-pill rc-pill-error">{cu.summary.no_price} without a price</span>
                    )}
                  </span>
                  <span className="material-symbols-rounded rc-cust-chevron" aria-hidden="true">{isOpen ? 'expand_less' : 'expand_more'}</span>
                </button>
                <button type="button" data-read-safe className="btn-icon rc-cust-open"
                  title={`Open ${cu.name}`} aria-label={`Open ${cu.name}`}
                  onClick={() => openEntity('contact', cu.account_id)}>
                  <span className="material-symbols-rounded">person</span>
                </button>
              </div>
              {isOpen && (
                <div className="rc-cust-body">
                  {!phone && (
                    <div className={`rc-col-heads${showCosts ? '' : ' no-costs'}`} aria-hidden="true">
                      <span>Contract</span><span>Ordered</span><span>Price</span>
                      {showCosts && <span>Cost</span>}
                      {showCosts && <span>Margin</span>}
                      <span />
                    </div>
                  )}
                  {cu.contracts.map(c => {
                    const open = openSet.has(c.key);
                    return (
                      <div key={c.key} className="rc-contract-wrap">
                        <ContractRow c={c} open={open} onToggle={() => toggleContract(c.key)} showCosts={showCosts} />
                        {open && (
                          <RepContractDetail contract={c} state={details[detailKey(c.key)]} showCosts={showCosts} />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {paging && paging.pages > 1 && !phone && (
        <div className="rc-pager rc-pager-foot">
          <PaginationControls
            pagination={{ page: paging.page, limit: paging.limit, total: paging.total, totalPages: paging.pages }}
            onPageChange={(p) => { setPage(p); toTop(); }}
          />
        </div>
      )}
      {phone && <PhonePager paging={paging} onPage={(p) => { setPage(p); toTop(); }} />}

      {phone && (
        <Modal
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Filters"
          className="rep-contracts-filters"
          footer={
            <div className="buttons slideout">
              <div>{clearButton}</div>
              <div>
                <button type="button" data-read-safe className="btn-slideout" onClick={() => setSheetOpen(false)}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">check</span></div>
                  <span className="btn-label">
                    {totals ? `Show ${totals.contracts} contract${totals.contracts === 1 ? '' : 's'}` : 'Done'}
                  </span>
                </button>
              </div>
            </div>
          }
        >
          <div className="rc-sheet">
            {filterControls}
            {chipControls}
          </div>
        </Modal>
      )}
    </div>
  );
}

export default RepContracts;
